import type { Tunnels } from "../tunnels/tunnels";
import type { FirewallEventGroup, FirewallEvents } from "../events/events.types";
import { isIpsecEvent } from "../events/eventsFormat";
import type { FirewallNat, FirewallPublication } from "../nat/nat.types";
import type { FirewallPolicies, FirewallPolicy } from "../policies/policies.types";
import type { Reading } from "../readings/useFwaasReading";
import { nodeOfError, rulesThrough } from "../attention/needsAttention";

/**
 * What each node's inspector shows, derived in the browser from the readings the page
 * already has (CTX-1264): no request of its own, and no address — every reading arrives
 * without them.
 */

/**
 * The node an event belongs to. An IPsec event goes where Observations puts it
 * (`nodeOfError`), so the inspector and the alert always agree; anything else — and an
 * event whose node is not on the map — is the firewall's own. Each event shows once.
 */
export function nodeOfEvent(group: FirewallEventGroup, tunnels: Tunnels | null, drawn: Set<string>): string {
    const node = !group.tunnel && !isIpsecEvent(group.text) ? "firewall" : nodeOfError(group, tunnels);
    return node === "firewall" || drawn.has(node) ? node : "firewall";
}

export function eventsOfNode(
    events: Reading<FirewallEvents>,
    tunnels: Tunnels | null,
    node: string,
    drawn: Set<string>
): Reading<FirewallEventGroup[]> {
    if (events == null) return events;
    return events.items.filter((group) => nodeOfEvent(group, tunnels, drawn) === node);
}

export const siteToSiteNames = (tunnels: Tunnels | null) =>
    (tunnels?.items ?? []).filter((t) => t.kind === "site-to-site").map((t) => t.name);

/** The rules that pass through a set of tunnels, keeping the reading's state when there are none to look at. */
export function tunnelRules(policies: Reading<FirewallPolicies>, names: string[]): Reading<FirewallPolicy[]> {
    return policies == null ? policies : rulesThrough(policies.items, names);
}

/**
 * What a rule publishing an app names as its destination: the VIP itself (classic NAT),
 * or an address object that is the app's internal host (central NAT, CTX-1273).
 */
const publishedAs = (app: FirewallPublication): string[] => [
    ...(app.name ? [app.name] : []),
    ...(app.targets ?? []),
];

/** The rules that let a published app in: they name it, or its host, as their destination. */
const rulesPublishing = (policies: FirewallPolicy[], app: FirewallPublication) => {
    const names = new Set(publishedAs(app));
    return policies.filter((p) => p.destinations.some((d) => d != null && names.has(d)));
};

export interface PublishedApp {
    name: string | null;
    protocol: string | null;
    /** "443", "443 → 8443" when forwarded to another port, "All ports" for a whole address. */
    ports: string;
    enabled: boolean;
    /** The hits of the rules that publish it; `null` while the rules are unknown or none does. */
    hits: number | null;
}

function portsOf(app: FirewallPublication): string {
    if (!app.port_forward || !app.external_port) return "All ports";
    if (app.port_forward && app.internal_port && app.internal_port !== app.external_port) {
        return `${app.external_port} → ${app.internal_port}`;
    }
    return app.external_port;
}

export function publishedApps(nat: FirewallNat, policies: FirewallPolicies | null | undefined): PublishedApp[] {
    return nat.items.map((app) => {
        const rules = policies ? rulesPublishing(policies.items, app) : [];
        return {
            name: app.name,
            protocol: app.protocol ? app.protocol.toUpperCase() : null,
            ports: portsOf(app),
            enabled: app.enabled,
            hits: rules.length ? rules.reduce((sum, rule) => sum + (rule.hits ?? 0), 0) : null,
        };
    });
}

/** Every rule that publishes one of the apps, once each, in the firewall's order. */
export function publishingRules(nat: Reading<FirewallNat>, policies: Reading<FirewallPolicies>): Reading<FirewallPolicy[]> {
    if (nat === undefined || policies === undefined) return undefined;
    if (nat === null || policies === null) return null;
    const names = new Set(nat.items.flatMap(publishedAs));
    return policies.items.filter((p) => p.destinations.some((d) => d != null && names.has(d)));
}
